import {
	failureActionHandler,
	requestActionHandler,
	successActionHandler,
} from 'store';
import injectReducer from 'store/injectReducer';
import * as types from '../ActionTypes';
import {
	ICompanyClients,
	ITemplateSchedulingData,
	ISurveyLimits,
	ISurveyRole,
	ISurveysReducer,
} from '../models';

export const InitialSurveysReducer: ISurveysReducer = {};

export default injectReducer(InitialSurveysReducer, {
	[types.POST_SURVEY_SCHEDULING.REQUEST]: requestActionHandler,
	[types.UPDATE_SURVEY_SCHEDULING.REQUEST]: requestActionHandler,

	[types.GET_SURVEY_LIMITS.REQUEST]: requestActionHandler,
	[types.GET_SURVEY_ROLES.REQUEST]: requestActionHandler,

	[types.DELETE_SURVEY_ROLE.REQUEST]: requestActionHandler,
	[types.POST_SURVEY_ROLE.REQUEST]: requestActionHandler,
	[types.EDIT_SURVEY_ROLE.REQUEST]: requestActionHandler,

	[types.GET_COMPANY_OFFICES.REQUEST]: requestActionHandler,
	[types.GET_OFFICES_AND_LIMITS_DATA.REQUEST]: requestActionHandler,
	[types.GET_COMPANY_CLIENTS.REQUEST]: requestActionHandler,

	[types.GET_SURVEY_DATA.FAILURE]: failureActionHandler,
	[types.POST_SURVEY_SCHEDULING.FAILURE]: failureActionHandler,
	[types.UPDATE_SURVEY_SCHEDULING.FAILURE]: failureActionHandler,

	[types.GET_SURVEY_LIMITS.FAILURE]: failureActionHandler,
	[types.GET_SURVEY_ROLES.FAILURE]: failureActionHandler,

	[types.DELETE_SURVEY_ROLE.FAILURE]: failureActionHandler,
	[types.POST_SURVEY_ROLE.FAILURE]: failureActionHandler,
	[types.EDIT_SURVEY_ROLE.FAILURE]: failureActionHandler,

	[types.GET_COMPANY_OFFICES.FAILURE]: failureActionHandler,
	[types.GET_OFFICES_AND_LIMITS_DATA.FAILURE]: failureActionHandler,
	[types.GET_COMPANY_CLIENTS.FAILURE]: failureActionHandler,

	[types.GET_SURVEY_DATA.REQUEST]: (
		state: ISurveysReducer,
	): ISurveysReducer => ({
		...state,
		isSurveyDataLoading: true,
	}),

	[types.GET_PLANNED_SURVEY_DATA.REQUEST]: (
		state: ISurveysReducer,
	): ISurveysReducer => ({
		...state,
		isPlannedSurveyDataLoading: true,
	}),

	[types.GET_OFFICES_AND_LIMITS_DATA.REQUEST]: (
		state: ISurveysReducer,
	): ISurveysReducer => ({
		...state,
		isOfficesAndLimitsDataLoading: true,
	}),

	[types.GET_SURVEY_DATA.FAILURE]: (
		state: ISurveysReducer,
		{ payload }: { payload: Error },
	): ISurveysReducer => ({
		...state,
		error: payload,
		isSurveyDataLoading: false,
	}),

	[types.GET_PLANNED_SURVEY_DATA.FAILURE]: (
		state: ISurveysReducer,
		{ payload }: { payload: Error },
	): ISurveysReducer => ({
		...state,
		error: payload,
		isPlannedSurveyDataLoading: false,
	}),

	[types.POST_SURVEY_SCHEDULING.SUCCESS]: (
		state: ISurveysReducer,
		{ payload: { id } }: { payload: { id: string } },
	) => ({
		...successActionHandler(state),
		createdSurveyId: id,
		isSurveyCreated: true,
	}),

	[types.UPDATE_SURVEY_SCHEDULING.SUCCESS]: (state: ISurveysReducer) => ({
		...successActionHandler(state),
		isSurveyUpdated: true,
	}),

	[types.GET_SURVEY_DATA.SUCCESS]: (state: ISurveysReducer) => ({
		...successActionHandler(state),
		isSurveyDataLoading: false,
	}),

	[types.GET_PLANNED_SURVEY_DATA.SUCCESS]: (
		state: ISurveysReducer,
		{ payload }: { payload: ITemplateSchedulingData },
	) => ({
		...successActionHandler(state),
		plannedSurveyData: payload,
		isPlannedSurveyDataLoading: false,
	}),

	[types.GET_SURVEY_LIMITS.SUCCESS]: (
		state: ISurveysReducer,
		{ payload }: { payload: ISurveyLimits },
	) => ({
		...successActionHandler(state),
		surveyLimits: payload,
	}),

	[types.GET_COMPANY_OFFICES.SUCCESS]: (
		state: ISurveysReducer,
		{ payload }: { payload: Record<string, number>[] },
	) => ({
		...successActionHandler(state),
		companyOffices: payload,
	}),

	[types.GET_OFFICES_AND_LIMITS_DATA.SUCCESS]: (
		state: ISurveysReducer,
		{
			payload,
		}: {
			payload: {
				offices: Record<string, number>;
				limits: ISurveyLimits;
			};
		},
	): ISurveysReducer => ({
		...state,
		companyOffices: payload.offices,
		surveyLimits: payload.limits,
		isOfficesAndLimitsDataLoading: false,
	}),

	[types.GET_COMPANY_CLIENTS.SUCCESS]: (
		state: ISurveysReducer,
		{ payload }: { payload: ICompanyClients },
	) => ({
		...successActionHandler(state),
		companyClients: payload,
	}),

	[types.GET_SURVEY_ROLES.SUCCESS]: (
		state: ISurveysReducer,
		{ payload }: { payload: ISurveyRole[] },
	) => ({
		...successActionHandler(state),
		surveyRoles: payload,
	}),

	[types.POST_SURVEY_ROLE.SUCCESS]: (
		state: ISurveysReducer,
		{ payload }: { payload: ISurveyRole },
	) => {
		const roles = state?.surveyRoles?.slice();
		roles?.unshift(payload);
		return {
			...successActionHandler(state),
			surveyRoles: roles,
		};
	},

	[types.EDIT_SURVEY_ROLE.SUCCESS]: (
		state: ISurveysReducer,
		{ payload }: { payload: ISurveyRole },
	) => {
		const { id, name } = payload;
		const roles = state?.surveyRoles?.slice();
		const role = roles?.find(({ id: roleId }) => roleId === id);
		role!.name = name;
		return {
			...successActionHandler(state),
			surveyRoles: roles,
		};
	},

	[types.DELETE_SURVEY_ROLE.SUCCESS]: (
		state: ISurveysReducer,
		{ payload: payloadId }: { payload: number },
	) => {
		return {
			...successActionHandler(state),
			surveyRoles: state?.surveyRoles?.filter(({ id }) => id !== payloadId),
		};
	},

	[types.RESET_ROLES_ERROR]: (state: ISurveysReducer) =>
		successActionHandler(state),

	[types.RESET_SCHEDULING]: (state: ISurveysReducer): ISurveysReducer => ({
		...successActionHandler(state),
		isSurveyCreated: undefined,
		isSurveyUpdated: undefined,
	}),

	[types.RESET_SCHEDULING_ERROR]: (
		state: ISurveysReducer,
	): ISurveysReducer => ({
		...successActionHandler(state),
		error: undefined,
	}),

	[types.RESET_PLANNED_SURVEY]: (state: ISurveysReducer): ISurveysReducer => ({
		...successActionHandler(state),
		plannedSurveyData: undefined,
	}),
});
